<script>
    // A card on the phone (docs/phone-plan.md): its thumbnail at its real
    // size, the band (suit, name, traits) redrawn over the scan's banner,
    // away teams, drones, orbiting ships and tokens on it, and whatever is
    // beamed to it sticking out above, the only way cards ever overlap. `h`
    // below the whole card's height crops it to a tile. Cards with no scan are
    // drawn by NewCardImage and cropped the same way.
    // Copied from hutchies/imperium src/imperium/ui/phone/PhoneFace.svelte.
    //
    // A tap reads the card (the inspector), or picks it when a prompt offers
    // it. A long press is the shell's (hold.js): the card with its manual actions.
    import { state, ui, res } from '../stores.js';
    import { toggleSelect } from '../ui.js';
    import { allTokenTypes } from '../card_actions.js';
    import { getNewIcon, getShipCode } from '../card_images.js';
    import { currentTraits } from '../parse.js';
    import NewCardImage from '../newcards/NewCardImage.svelte';
    import { inspected, offeredIds, tuckedUnder, CARD_RATIO } from './phone.js';
    import { band, bandColour, faceImage, thumbURL } from './face.js';
    import { closeInspector } from './zoom.js';
    import { haptic } from './haptics.js';

    export let c;
    export let w = 60;
    export let h = null;          // null: the whole card
    export let facedown = false;
    export let ready = false;     // an activation usable now
    export let lift = false;
    export let traits = 3;        // trait icons in the band
    export let tucked = true;     // draw what is beamed to it
    export let inspect = true;
    export let duty = false;      // a duty officer: green edge
    export let note = '';         // a mark of its own, e.g. the captain's log count

    $: whole = Math.round(w * CARD_RATIO);
    $: height = h ?? whole;
    $: img = c ? faceImage(c, facedown) : null;
    $: b = c && !facedown ? band(c, traits) : null;
    $: bandH = Math.max(13, Math.min(18, Math.round(w * 0.15)));
    $: colour = bandColour(c);
    $: offered = offeredIds($ui);
    $: id = c?.uniqueID;
    $: dimmed = offered.length > 0 && !offered.includes(id);
    $: held = $inspected?.id == id;   // the card in focus
    $: me = $state.players[$ui.seat]?.captain;
    $: away = counts(c?.away_teams, me);
    $: drones = counts(c?.drones, me);
    $: ships = (c?.orbiting ?? []).map(o => ({ code: getShipCode(o.card, o.ship), mine: o.sourcePlayer == $ui.seat }));
    $: tokens = c ? allTokenTypes().filter(r => c[r] > 0).map(r => [r, c[r]]) : [];
    $: under = tucked ? tuckedUnder(c) : [];

    // Yours and theirs from a { captain: n } map.
    function counts(m, mine){
        let out = { mine: 0, theirs: 0 };
        for(let [k, n] of Object.entries(m ?? {})) out[k == mine ? 'mine' : 'theirs'] += n;
        return out;
    }

    function tap(e){
        if(!c) return;
        e.stopPropagation();
        if(offered.includes(id)){ haptic(); toggleSelect(c, facedown); return; }
        if(facedown) return; // face down, it is not yours to read
        if(offered.length || !inspect) return;
        if($inspected?.id == id) closeInspector();
        else $inspected = { id, from: e.currentTarget.getBoundingClientRect(), manual: false };
    }
    function tapTucked(e, t){
        e.stopPropagation();
        if(offered.length){ if(offered.includes(t.uniqueID)){ haptic(); toggleSelect(t); } return; }
        $inspected = { id: t.uniqueID, from: e.currentTarget.getBoundingClientRect(), manual: false };
    }
</script>

{#if c}
<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div class="pc" class:offer={offered.includes(id)} class:dim={dimmed} class:lift class:held class:duty class:used={c.activated}
    data-card={id} data-facedown={facedown ? '' : null} style="width: {w}px; height: {height}px; --sc: {colour}; --bh: {bandH}px; --w: {w}px;"
    role="button" tabindex="-1" aria-label={b?.name ?? 'Card'} on:click={tap} on:contextmenu|preventDefault>
    {#if under.length}
        {@const t = under[0]}
        <div class="pc tuck" data-card={t.uniqueID} style="--sc: {bandColour(t)};" on:click={e => tapTucked(e, t)}>
            <span>{t.name ?? ''}</span>{#if under.length > 1}<b>+{under.length - 1}</b>{/if}
        </div>
    {/if}
    <div class="art">
        {#if img}
            <img src={thumbURL(img)} alt="" draggable="false" decoding="async" class:full={h == null || h >= whole} />
        {:else}
            <div class="drawn" style="--width: {w}px; --height: {whole}px;">
                <NewCardImage {c} card={{...$res.atCards[c.id], traits: currentTraits(c), skills: c.values}} operations={$res.atOps[c.id]} handleClick={() => {}} />
            </div>
        {/if}
    </div>
    {#if b}
        <div class="band">
            {#if b.suit}<img src={b.suit} alt="" class="suit" />{/if}
            <span class="nm">{b.name}</span>
            {#each b.traits as t}<img src={t} alt="" />{/each}
            {#if ready}<span class="ready" title="Can activate now"><svg viewBox="0 0 24 24"><path d="M13 2L4 14h6l-1 8 9-12h-6z" /></svg></span>{/if}
        </div>
    {/if}
    {#if away.mine || away.theirs || drones.mine || drones.theirs || ships.length || tokens.length || c.activated || note}
        <div class="marks">
            {#if away.mine}<span class="mark"><img src={getNewIcon('away')} alt="away teams" />{away.mine}</span>{/if}
            {#if away.theirs}<span class="mark theirs"><img src={getNewIcon('away')} alt="their away teams" />{away.theirs}</span>{/if}
            {#if drones.mine}<span class="mark"><img src={getNewIcon('drone')} alt="drones" />{drones.mine}</span>{/if}
            {#if drones.theirs}<span class="mark theirs"><img src={getNewIcon('drone')} alt="their drones" />{drones.theirs}</span>{/if}
            {#each ships as s}<span class="mark ship" class:theirs={!s.mine}>{s.code}</span>{/each}
            {#each tokens as [r, n]}<span class="mark"><img src={getNewIcon(r)} alt={r} />{#if n > 1}{n}{/if}</span>{/each}
            {#if note}<span class="mark">{note}</span>{/if}
            {#if c.activated}<span class="mark exh" title="Exhausted"></span>{/if}
        </div>
    {/if}
</div>
{/if}

<style>
    .pc { position: relative; flex: none; border-radius: 5px; box-shadow: 0 1px 4px rgba(0,0,0,.6); transition: transform .15s ease-out, opacity .15s; cursor: pointer; -webkit-tap-highlight-color: transparent; user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; }
    .art { position: absolute; inset: 0; border-radius: 5px; overflow: hidden; background: #111; }
    .art img { width: 100%; height: auto; display: block; pointer-events: none; -webkit-user-drag: none; }
    .art img.full { height: 100%; object-fit: cover; }
    .drawn { pointer-events: none; }
    .used .art { opacity: .55; }
    .band { position: absolute; left: 0; right: 0; top: 0; height: var(--bh); display: flex; align-items: center; gap: 2px; padding: 0 3px; border-radius: 5px 5px 0 0; background: var(--sc); color: #fff;
        font: 600 calc(var(--bh) * .64)/1 'Barlow Condensed', 'Arial Narrow', system-ui, sans-serif; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-shadow: 0 1px 1px rgba(0,0,0,.45); }
    .band img { width: calc(var(--bh) * .72); height: calc(var(--bh) * .72); flex: none; }
    .band img.suit { filter: invert(1); } /* the suit SVGs are black */
    .nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
    .ready { width: calc(var(--bh) * .74); height: calc(var(--bh) * .74); flex: none; border-radius: 50%; background: #f2c14e; display: grid; place-items: center; }
    .ready svg { width: 70%; height: 70%; fill: #1b1404; }
    .marks { position: absolute; right: 2px; bottom: 2px; display: flex; gap: 2px; pointer-events: none; }
    .mark { display: inline-flex; align-items: center; gap: 1px; height: 13px; padding: 0 3px; border-radius: 4px; background: rgba(0,0,0,.8); color: #fff; font: 700 10px 'Barlow Condensed', 'Arial Narrow', system-ui, sans-serif; }
    .mark img { width: 9px; height: 9px; }
    .mark.theirs { color: #ffb3c0; }
    .mark.ship { color: #bcd6ff; }
    .mark.ship.theirs { color: #ffc0cb; }
    .mark.exh::before { content: ""; width: 9px; height: 6px; border: 1.5px solid #fff; border-radius: 1.5px; }
    /* What is beamed to it: its band shows above the card, as on the table. */
    .tuck { position: absolute; left: 4px; right: 4px; top: -11px; height: 15px; width: auto; padding: 1px 4px 0; border-radius: 4px 4px 0 0; background: var(--sc); color: #fff;
        font: 600 9.5px/11px 'Barlow Condensed', 'Arial Narrow', system-ui, sans-serif; text-transform: uppercase; display: flex; gap: 3px; overflow: hidden; white-space: nowrap; box-shadow: 0 -1px 2px rgba(0,0,0,.5); }
    .tuck span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
    .pc.lift { transform: translateY(-14px); box-shadow: 0 0 0 2px #f2c14e, 0 6px 14px rgba(0,0,0,.7); z-index: 2; }
    .pc.held { box-shadow: 0 0 0 2px #fff7e6, 0 0 12px rgba(255,247,230,.6); z-index: 2; }
    .pc.offer { box-shadow: 0 0 0 2px #55d6ff, 0 0 10px rgba(85,214,255,.5); }
    .pc.dim { opacity: .35; }
    .pc.duty::after { content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 3px; border-radius: 0 0 5px 5px; background: #6fd38a; pointer-events: none; }
    @media (prefers-reduced-motion: reduce) { .pc { transition: none; } }
</style>
